import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Edit2, Trash2, Save, X } from "lucide-react";
import AddBudgetModal from "./AddBudgetModal";
import { fetchBudgets, updateBudget, deleteBudget, fetchExpenseCategories } from "../services/api";

// Mobile Card Component - Manages its own edit state
const BudgetCard = React.memo(({
  budget,
  index,
  categories,
  onSave,
  onDelete,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editCategory, setEditCategory] = useState(budget.id_expense_category);
  const [editAmount, setEditAmount] = useState(budget.budget_amt);
  const [saveError, setSaveError] = useState(null);

  const handleEdit = () => {
    setIsEditing(true);
    setEditCategory(budget.id_expense_category);
    setEditAmount(budget.budget_amt);
    setSaveError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditCategory(budget.id_expense_category);
    setEditAmount(budget.budget_amt);
    setSaveError(null);
  };

  const handleSave = async () => {
    try {
      await onSave(budget.id_budget, {
        id_expense_category: editCategory,
        budget_amt: editAmount
      });
      setIsEditing(false);
      setSaveError(null);
    } catch (err) {
      setSaveError(err.message || "Failed to update budget");
    }
  };

  const handleDelete = () => {
    onDelete(budget.id_budget);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, delay: index * 0.04 }}
      className="bg-gray-750 rounded-lg p-4 border border-gray-700 hover:border-gray-600 transition-colors"
    >
      {isEditing ? (
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Category</label>
            <select
              value={editCategory}
              onChange={(e) => setEditCategory(Number(e.target.value))}
              className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 
                         focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
            >
              {categories.map((cat) => (
                <option key={cat.id_expense_category} value={cat.id_expense_category}>
                  {cat.expense_category_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Budget Amount</label>
            <input
              type="number"
              step="0.01"
              value={editAmount}
              onChange={(e) => setEditAmount(Number(e.target.value))}
              className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 
                         focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
              autoFocus
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button
              onClick={handleSave}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-green-400 
                         bg-green-400/10 hover:bg-green-400/20 rounded-md transition-colors text-sm"
            >
              <Save size={16} />
              Save
            </button>
            <button
              onClick={handleCancel}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-gray-400 
                         bg-gray-600/50 hover:bg-gray-600 rounded-md transition-colors text-sm"
            >
              <X size={16} />
              Cancel
            </button>
          </div>
          {saveError && (
            <p className="text-red-400 text-xs">{saveError}</p>
          )}
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-start mb-3">
            <div>
              <p className="text-gray-200 font-medium text-base">{budget.expense_category_name}</p>
              <p className="text-green-400 font-semibold text-lg mt-1">
                ${Number(budget.budget_amt).toFixed(2)}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleEdit}
                className="p-2 text-blue-400 hover:bg-blue-400/10 rounded-md transition-colors"
                title="Edit"
              >
                <Edit2 size={18} />
              </button>
              <button
                onClick={handleDelete}
                className="p-2 text-red-400 hover:bg-red-400/10 rounded-md transition-colors"
                title="Delete"
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function - only re-render if budget data actually changed
  return (
    prevProps.budget.id_budget === nextProps.budget.id_budget &&
    prevProps.budget.budget_amt === nextProps.budget.budget_amt &&
    prevProps.budget.id_expense_category === nextProps.budget.id_expense_category &&
    prevProps.budget.expense_category_name === nextProps.budget.expense_category_name &&
    prevProps.categories.length === nextProps.categories.length
  );
});

BudgetCard.displayName = 'BudgetCard';

// Desktop Table Row Component - Manages its own edit state
const BudgetTableRow = React.memo(({
  budget,
  index,
  categories,
  onSave,
  onDelete,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editCategory, setEditCategory] = useState(budget.id_expense_category);
  const [editAmount, setEditAmount] = useState(budget.budget_amt);
  const [saveError, setSaveError] = useState(null);

  const handleEdit = () => {
    setIsEditing(true);
    setEditCategory(budget.id_expense_category);
    setEditAmount(budget.budget_amt);
    setSaveError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditCategory(budget.id_expense_category);
    setEditAmount(budget.budget_amt);
    setSaveError(null);
  };

  const handleSave = async () => {
    try {
      await onSave(budget.id_budget, {
        id_expense_category: editCategory,
        budget_amt: editAmount
      });
      setIsEditing(false);
      setSaveError(null);
    } catch (err) {
      setSaveError(err.message || "Failed to update budget");
    }
  };

  const handleDelete = () => {
    onDelete(budget.id_budget);
  };

  return (
    <motion.tr
      initial={{ opacity: 0, x: -15 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 15 }}
      transition={{ duration: 0.3, delay: index * 0.04 }}
      className="border-t border-gray-700 hover:bg-gray-750 transition-colors text-sm sm:text-base"
    >
      <td className="px-4 sm:px-6 py-3 sm:py-4 text-gray-200">
        {isEditing ? (
          <select
            value={editCategory}
            onChange={(e) => setEditCategory(Number(e.target.value))}
            className="w-full px-2 py-1.5 bg-gray-700 text-white rounded-md 
                       border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
          >
            {categories.map((cat) => (
              <option key={cat.id_expense_category} value={cat.id_expense_category}>
                {cat.expense_category_name}
              </option>
            ))}
          </select>
        ) : (
          budget.expense_category_name
        )}
      </td>
      <td className="px-4 sm:px-6 py-3 sm:py-4 text-green-400 font-semibold">
        {isEditing ? (
          <input
            type="number"
            step="0.01"
            value={editAmount}
            onChange={(e) => setEditAmount(Number(e.target.value))}
            className="w-24 sm:w-32 px-2 py-1.5 bg-gray-700 text-white rounded-md 
                       border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
          />
        ) : (
          `$${Number(budget.budget_amt).toFixed(2)}`
        )}
      </td>
      <td className="px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-end gap-1.5 sm:gap-2">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                className="p-1.5 sm:p-2 text-green-400 hover:bg-green-400/10 rounded-md transition-colors"
                title="Save"
              >
                <Save size={16} />
              </button>
              <button
                onClick={handleCancel}
                className="p-1.5 sm:p-2 text-gray-400 hover:bg-gray-600 rounded-md transition-colors"
                title="Cancel"
              >
                <X size={16} />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleEdit}
                className="p-1.5 sm:p-2 text-blue-400 hover:bg-blue-400/10 rounded-md transition-colors"
                title="Edit"
              >
                <Edit2 size={16} />
              </button>
              <button
                onClick={handleDelete}
                className="p-1.5 sm:p-2 text-red-400 hover:bg-red-400/10 rounded-md transition-colors"
                title="Delete"
              >
                <Trash2 size={16} />
              </button>
            </>
          )}
        </div>
        {saveError && (
          <p className="text-red-400 text-xs mt-1 text-right">
            {saveError}
          </p>
        )}
      </td>
    </motion.tr>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function
  return (
    prevProps.budget.id_budget === nextProps.budget.id_budget &&
    prevProps.budget.budget_amt === nextProps.budget.budget_amt &&
    prevProps.budget.id_expense_category === nextProps.budget.id_expense_category &&
    prevProps.budget.expense_category_name === nextProps.budget.expense_category_name &&
    prevProps.categories.length === nextProps.categories.length
  );
});

BudgetTableRow.displayName = 'BudgetTableRow';

// Empty State Component
const EmptyState = React.memo(() => (
  <div className="p-6 sm:p-8 text-center text-gray-400">
    <p className="text-base sm:text-lg mb-2">No budgets set yet</p>
    <p className="text-sm text-gray-500">Tap the + button to add your first budget</p>
  </div>
));

EmptyState.displayName = 'EmptyState';

// Loading State Component
const LoadingState = React.memo(() => (
  <div className="p-6 sm:p-8 text-center text-gray-400">
    <div className="animate-pulse">Loading budgets...</div>
  </div>
));

LoadingState.displayName = 'LoadingState';

// Error State Component
const ErrorState = React.memo(({ error }) => (
  <div className="p-6 sm:p-8 text-center text-red-400">{error}</div>
));

ErrorState.displayName = 'ErrorState';

// Main Component
export default function BudgetDashboard({ onBudgetsChanged }) {
  const [budgets, setBudgets] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadBudgets = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchBudgets();
      setBudgets(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching budgets:", err);
      setError("Failed to load budgets");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const data = await fetchExpenseCategories();
      setCategories(data);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  }, []);

  useEffect(() => {
    loadBudgets();
    loadCategories();
  }, [loadBudgets, loadCategories]);

  const handleSave = useCallback(async (budgetId, values) => {
    await updateBudget(budgetId, values);
    await loadBudgets();
    onBudgetsChanged?.();
  }, [loadBudgets, onBudgetsChanged]);

  const handleDelete = useCallback(async (budgetId) => {
    if (!window.confirm("Are you sure you want to delete this budget?")) return;
    try {
      await deleteBudget(budgetId);
      await loadBudgets();
      onBudgetsChanged?.();
    } catch (err) {
      console.error("Error deleting budget:", err);
      alert("Failed to delete budget");
    }
  }, [loadBudgets, onBudgetsChanged]);

  const handleAddSuccess = useCallback(() => {
    loadBudgets();
    setShowAddForm(false);
    onBudgetsChanged?.();
  }, [loadBudgets, onBudgetsChanged]);

  const toggleAddForm = useCallback(() => setShowAddForm(prev => !prev), []);

  return (
    <motion.div
      className="w-full max-w-4xl mx-auto mt-6 mb-10 px-3 sm:px-4"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 1.2, ease: "easeOut" }}
    >
      {/* Header with Plus Button */}
      <div className="flex flex-col items-center justify-center mb-5 text-center">
        <h2 className="text-xl sm:text-2xl font-bold text-white font-[Poppins] mb-3">
          Set Budget
        </h2>
        <button
          onClick={toggleAddForm}
          className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full 
                     bg-gradient-to-r from-blue-500 to-sky-600 hover:opacity-90 
                     transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-110 active:scale-95"
        >
          <Plus size={22} className="text-white" />
        </button>
      </div>

      {/* Add Budget Form */}
      <AddBudgetModal
        showForm={showAddForm}
        onToggle={toggleAddForm}
        onSuccess={handleAddSuccess}
      />

      {/* Budget List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden"
      >
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState error={error} />
        ) : budgets.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Mobile Card View (< 640px) - Now Scrollable */}
            <div className="block sm:hidden relative">
              <div className="block sm:hidden p-3 space-y-3 max-h-[55vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
                <AnimatePresence>
                  {budgets.map((budget, index) => (
                    <BudgetCard
                      key={budget.id_budget}
                      budget={budget}
                      index={index}
                      categories={categories}
                      onSave={handleSave}
                      onDelete={handleDelete}
                    />
                  ))}
                </AnimatePresence>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-gray-800 to-transparent pointer-events-none"></div>
            </div>
            {/* Desktop Table View (>= 640px) */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900">
                  <tr className="text-gray-300 text-left text-sm sm:text-base">
                    <th className="px-4 sm:px-6 py-3 sm:py-4 font-semibold">Category</th>
                    <th className="px-4 sm:px-6 py-3 sm:py-4 font-semibold">Budget</th>
                    <th className="px-4 sm:px-6 py-3 sm:py-4 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence>
                    {budgets.map((budget, index) => (
                      <BudgetTableRow
                        key={budget.id_budget}
                        budget={budget}
                        index={index}
                        categories={categories}
                        onSave={handleSave}
                        onDelete={handleDelete}
                      />
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          </>
        )}
      </motion.div>
    </motion.div >
  );
}